import { getFormatPattern, getFormattedValue, getLocale, } from '@thoughtspot/formatter-service';
import { getValueFormattedByConfig } from '@thoughtspot/number-format-util';
import { isTooltipCustomizationEnabled } from '@thoughtspot/session-service';
import { getEmptyValuePlaceHolder, getNullValuePlaceHolder, } from '@thoughtspot/special-data-value-format';
import { getCustomCalendarMapForColumn, getId, isColumnValidForNumberFormatting, } from '@thoughtspot/visualization-column-util';
import _ from 'lodash';
import { getDataRowIndex, getTooltipColumns } from '../core/axis-config-util';
import { getFormattingForColumn, getXAxisColumns, } from '../core/chart-model-utils';
import { getDataArray, getDisplayValue, getTooltipConfig, } from '../data/data-util';
import { ChartDataModel } from '../highcharts/highchart-data-model';
export class ChartNode {
    constructor(key, name, baseColumnIndex, value, originalValue, tooltipData, x1, x0, y1, y0, color, index) {
        this.key = key;
        this.name = name;
        this.baseColumnIndex = baseColumnIndex;
        this.value = value;
        this.originalValue = originalValue;
        this.tooltipData = tooltipData;
        this.x1 = x1;
        this.x0 = x0;
        this.y1 = y1;
        this.y0 = y0;
        this.color = color;
        this.index = index;
    }
}
export class ChartLink {
    constructor(key, value, formattedValue, source, target, tooltipData, sourceColumnId, targetColumnId, valueColumnId, y1, y0, width) {
        this.key = key;
        this.value = value;
        this.formattedValue = formattedValue;
        this.source = source;
        this.target = target;
        this.tooltipData = tooltipData;
        this.sourceColumnId = sourceColumnId;
        this.targetColumnId = targetColumnId;
        this.valueColumnId = valueColumnId;
        this.y1 = y1;
        this.y0 = y0;
        this.width = width;
    }
}
export function isChartLink(chartElement) {
    return 'source' in chartElement;
}
export class NetworkChartDataModel extends ChartDataModel {
    constructor(chartModel, data, queryDefinitions, options, chartOptions, getEventMap) {
        super();
        this.chartModel = chartModel;
        this.data = data;
        this.queryDefinitions = queryDefinitions;
        this.options = options;
        this.links = [];
        this.nodesMapById = new Map();
        this.nodeByColumn = [];
        this.displayToCustomCalendarValueMap = {};
        this.dataArray = getDataArray(data);
        this.processData();
    }
    get nodes() {
        return [...this.nodesMapById.values()];
    }
    get hasNoData() {
        return !this.links || this.links.length === 0;
    }
    getNodeByKey(id) {
        return this.nodesMapById.get(id);
    }
    getDisplayCustomCalendarMapForColumn(column) {
        return getCustomCalendarMapForColumn(column, this.displayToCustomCalendarValueMap);
    }
    addNodeToDictionaryIfNeeded(key, name, baseColumnIndex, value, tooltipDataRow) {
        var _a;
        let node = this.getNodeByKey(key);
        if (!node) {
            node = new ChartNode(key, name, baseColumnIndex, value, value, _.cloneDeep(tooltipDataRow));
            this.nodesMapById.set(key, node);
            if (!this.nodeByColumn[baseColumnIndex]) {
                this.nodeByColumn[baseColumnIndex] = [];
            }
            this.nodeByColumn[baseColumnIndex].push(node);
        }
        else if (tooltipDataRow) {
            (_a = node.tooltipData) === null || _a === void 0 ? void 0 : _a.map((tooltipDataForColumn, i) => {
                return {
                    ...tooltipDataForColumn,
                    value: Number(tooltipDataForColumn.value) +
                        Number(tooltipDataRow[i].value),
                };
            });
        }
        return node;
    }
    processData() {
        const queryDefinitions = this.queryDefinitions;
        let tooltipColumns = [];
        this.tooltipConfig = getTooltipConfig(this.chartModel);
        if (isTooltipCustomizationEnabled()) {
            tooltipColumns = this.queryDefinitions.map((queryDefinition, index) => {
                var _a;
                return getTooltipColumns(this.queryDefinitions, (_a = this.tooltipConfig) === null || _a === void 0 ? void 0 : _a.columnIds, this.chartModel.columns, index);
            });
        }
        this.links = this.dataArray.reduce((links, linksArray, idx) => links.concat(this.processLinksArray(linksArray.getData(), queryDefinitions[idx].xAxisColumns[0], queryDefinitions[idx].xAxisColumns[1], queryDefinitions[idx].yAxisColumns[0], tooltipColumns[idx])), []);
        this.maxNodesInColumns = _.values(this.nodeByColumn).reduce((acc, value) => {
            if (value.length > acc) {
                return value.length;
            }
            return acc;
        }, 0);
    }
    getDisplayValueForSankey(value, getTranslatedText) {
        if (typeof value === 'number') {
            return value.toString();
        }
        if (value === null) {
            return getNullValuePlaceHolder();
        }
        if (value === '') {
            return getEmptyValuePlaceHolder();
        }
        return getDisplayValue(value, getTranslatedText);
    }
    processLinksArray(links, sourceColumn, targetColumn, valueColumn, tooltipColumns) {
        const formatPattern = getFormatPattern(valueColumn);
        const columnLocale = getLocale(valueColumn);
        const chartModel = this.chartModel;
        const sourceColumnId = getId(sourceColumn);
        const sourceColumnDataIndex = getDataRowIndex(sourceColumn);
        const targetColumnId = getId(targetColumn);
        const targetColumnDataIndex = getDataRowIndex(targetColumn);
        const valueColumnId = getId(valueColumn);
        const valueColumnDataIndex = getDataRowIndex(valueColumn);
        const sourceColumnIndex = getXAxisColumns(chartModel).findIndex(c => getId(c) === sourceColumnId);
        const targetColumnIndex = getXAxisColumns(chartModel).findIndex(c => getId(c) === targetColumnId);
        this.formatter = value => {
            const formatConfig = getFormattingForColumn(chartModel, valueColumnId);
            if (formatConfig) {
                return getValueFormattedByConfig(value, formatConfig, {
                    formatPattern,
                    columnLocale,
                });
            }
            return getFormattedValue(valueColumn, value);
        };
        return links.map((link) => {
            const sourceValue = this.getDisplayValueForSankey(link[sourceColumnDataIndex], this.options.getTranslatedText);
            const targetValue = this.getDisplayValueForSankey(link[targetColumnDataIndex], this.options.getTranslatedText);
            const sourceName = this.getNodeName(sourceValue, sourceColumn);
            const targetName = this.getNodeName(targetValue, targetColumn);
            const value = link[valueColumnDataIndex];
            const sourceNodeKey = `${sourceColumnId}-${sourceName}`;
            const targetNodeKey = `${targetColumnId}-${targetName}`;
            if (!this.tooltipFormatter) {
                this.tooltipFormatter = {};
            }
            const tooltipDataRow = this.addTooltipConfigContent(tooltipColumns, sourceColumn, targetColumn, chartModel, link);
            const source = this.addNodeToDictionaryIfNeeded(sourceNodeKey, sourceName, sourceColumnIndex, sourceValue, sourceColumnIndex === 0 ? tooltipDataRow : null);
            const target = this.addNodeToDictionaryIfNeeded(targetNodeKey, targetName, targetColumnIndex, targetValue, tooltipDataRow);
            return new ChartLink(`${sourceNodeKey}-${targetNodeKey}`, value, this.getNodeName(value, valueColumn), source, target, tooltipDataRow, sourceColumnId, targetColumnId, valueColumnId);
        });
    }
    addTooltipConfigContent(tooltipColumns, sourceColumn, targetColumn, chartModel, link) {
        const tooltipData = [];
        const sourceColumnId = getId(sourceColumn);
        const targetColumnId = getId(targetColumn);
        tooltipColumns === null || tooltipColumns === void 0 ? void 0 : tooltipColumns.forEach((valueColumn, i) => {
            const columnId = getId(valueColumn);
            if (![sourceColumnId, targetColumnId].includes(columnId)) {
                const formatPattern = getFormatPattern(valueColumn);
                const columnLocale = getLocale(valueColumn);
                this.tooltipFormatter[columnId] = value => {
                    const formatConfig = getFormattingForColumn(chartModel, getId(valueColumn));
                    if (formatConfig) {
                        return getValueFormattedByConfig(value, formatConfig, {
                            formatPattern,
                            columnLocale,
                        });
                    }
                    return getFormattedValue(valueColumn, value);
                };
                const value = link[getDataRowIndex(valueColumn)];
                const key = valueColumn.name;
                if (!_.isNil(value)) {
                    tooltipData.push({
                        id: columnId,
                        key,
                        value,
                    });
                }
            }
        });
        return tooltipData;
    }
    getNodeName(value, _column) {
        const formatPattern = getFormatPattern(_column);
        const columnLocale = getLocale(_column);
        const formatConfig = getFormattingForColumn(this.chartModel, getId(_column));
        if (formatConfig && isColumnValidForNumberFormatting(_column)) {
            return getValueFormattedByConfig(value, formatConfig, {
                formatPattern,
                columnLocale,
            });
        }
        return getFormattedValue(_column, value);
    }
    getDataArray() {
        return this.dataArray;
    }
}
//# sourceMappingURL=network-chart-data-model.js.map